/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Keyboard Shortcuts Help Modal
 */

import React from "react";
import { IconKeyboard } from "@tabler/icons-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Separator,
  Badge,
} from "@lemonadetraining/web-ui";
import type { KeyboardShortcut } from "../../hooks/useKeyboardShortcuts";
import { getShortcutDisplay } from "../../hooks/useKeyboardShortcuts";

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
  shortcuts: KeyboardShortcut[];
}

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  open,
  onClose,
  shortcuts,
}) => {
  // Group shortcuts by category. Anything that doesn't match a known category
  // (e.g. Cmd+K, Cmd+L from the global shortcut help) lands in "Shortcuts" so
  // it isn't silently dropped.
  const navigationShortcuts = shortcuts.filter(
    (s) => s.key === "ArrowLeft" || s.key === "ArrowRight" || s.key === "t"
  );
  const actionShortcuts = shortcuts.filter((s) => s.key === "n" || s.key === "s" || s.key === "a");
  const uiShortcuts = shortcuts.filter((s) => s.key === "f" || s.key === "Escape" || s.key === "?");
  const otherShortcuts = shortcuts.filter(
    (s) =>
      !navigationShortcuts.includes(s) && !actionShortcuts.includes(s) && !uiShortcuts.includes(s)
  );

  // Only non-empty sections render, with a separator BETWEEN sections (never
  // before the first — the header already draws one, and a leading empty
  // section must not produce a doubled line).
  const sections = [
    { title: "Navigation", items: navigationShortcuts },
    { title: "Actions", items: actionShortcuts },
    { title: "Interface", items: uiShortcuts },
    { title: "Shortcuts", items: otherShortcuts },
  ].filter((section) => section.items.length > 0);

  const renderShortcutRow = (shortcut: KeyboardShortcut) => (
    <div
      key={shortcut.key + shortcut.description}
      className="flex justify-between items-center py-2"
    >
      <span className="text-sm">{shortcut.description}</span>
      <Badge variant="outline" className="font-mono text-xs">
        {getShortcutDisplay(shortcut)}
      </Badge>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconKeyboard className="h-5 w-5" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="flex flex-col gap-4">
          {sections.map((section, index) => (
            <React.Fragment key={section.title}>
              {index > 0 && <Separator />}
              <div>
                <h4 className="text-sm font-medium mb-2">{section.title}</h4>
                <div className="flex flex-col gap-1">{section.items.map(renderShortcutRow)}</div>
              </div>
            </React.Fragment>
          ))}

          {/* Tip */}
          <div className="bg-muted p-3 rounded-md mt-2">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Tip:</span> Press{" "}
              <code className="font-mono bg-background px-1 py-0.5 rounded">?</code> anytime to view
              this help dialog.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsModal;
